import { DEFAULT_LOCALE, type Locale } from '@ghostscreen/contracts';
import { i18n } from './i18n';
import { pickBrowserLocale, regionalTag, toSupportedLocale } from './languages';
import { loadLocaleResources } from './loadLocaleResources';

/** This device's last choice, so the right language shows before sign-in. */
const STORAGE_KEY = 'gs.locale';

function browserLanguages(): readonly string[] {
  return typeof navigator === 'undefined' ? [] : navigator.languages;
}

function readStoredLocale(): Locale | null {
  try {
    return toSupportedLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* storage unavailable (private mode) — the account copy still applies after sign-in */
  }
}

/** `<html lang dir>` drives right-to-left layout, fonts, and screen readers. */
function syncDocument(languageTag: string): void {
  document.documentElement.lang = languageTag;
  document.documentElement.dir = i18n.dir(languageTag);
}

/** The language currently shown, reduced to one we translate. */
export function currentLocale(): Locale {
  return toSupportedLocale(i18n.resolvedLanguage) ?? DEFAULT_LOCALE;
}

/** Downloads the language if needed, then switches the whole dashboard to it. */
export async function applyLocale(locale: Locale): Promise<void> {
  await loadLocaleResources(locale);
  const tag = regionalTag(locale, browserLanguages());
  await i18n.changeLanguage(tag);
  syncDocument(tag);
  storeLocale(locale);
}

/** Startup: this device's last choice, else the browser's language, else English. */
export function initialLocale(): Locale {
  return readStoredLocale() ?? pickBrowserLocale(browserLanguages());
}
